import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck } from "lucide-react";

const LAB = "https://images.unsplash.com/photo-1508450859948-4e04fabaa4ea?fm=jpg&q=80&w=1000&auto=format&fit=crop";

const STATS = [
  { value: "NABL", label: "Accredited Laboratory" },
  { value: "10+", label: "Testing Disciplines" },
  { value: "100+", label: "Projects Completed" },
  { value: "100%", label: "Traceable Reporting" },
];

function Counter({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-36 border-t border-[#27272a]">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div ref={ref} className="relative overflow-hidden h-[420px] md:h-[560px] border border-[#27272a]">
          <motion.img
            style={{ y, scale: 1.15 }}
            src={LAB}
            alt="Qualiz laboratory testing"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 to-transparent" />
          <div className="absolute bottom-6 left-6 flex items-center gap-3 bg-[#050505]/80 backdrop-blur-md border border-[#27272a] px-5 py-3">
            <ShieldCheck size={20} className="text-[#ff4500]" />
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase">ISO/IEC 17025 aligned</span>
          </div>
        </div>

        <div>
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#ff4500]">/ 02 — Who we are</span>
          <h2 className="mt-5 font-heading font-black uppercase tracking-tighter leading-[0.95] text-4xl md:text-5xl text-[#f4f4f5]">
            NABL accredited laboratory as per IS 17025:2017.
          </h2>
          <p className="mt-7 text-[#8e8e93] text-base md:text-lg font-light leading-relaxed">
            Qualiz Material Testing and Research Centre Pvt. Ltd. is an NABL accredited laboratory based in
            Akola, specialising in soil and construction material testing. We serve esteemed clients across
            government departments and the private sector — delivering results that stand up to the highest
            scrutiny.
          </p>

          <div className="mt-12 grid grid-cols-2 gap-px bg-[#27272a] border border-[#27272a]">
            {STATS.map((s) => (
              <Counter key={s.label}>
                <div className="bg-[#111111] p-6 md:p-8">
                  <div className="font-heading font-black text-4xl md:text-5xl tracking-tighter text-[#ff4500]">
                    {s.value}
                  </div>
                  <div className="mt-2 font-mono text-[10px] tracking-[0.15em] uppercase text-[#8e8e93]">
                    {s.label}
                  </div>
                </div>
              </Counter>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
